import { useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { ThemeContextProvider } from '@/theme/ThemeContext';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 30s background staleness is plenty for low-write data like
        // connections/rules. Mutations invalidate explicitly.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Don't retry auth failures — the auth guard will redirect.
          const message = error instanceof Error ? error.message : '';
          if (/\b401\b/.test(message) || /\b403\b/.test(message)) return false;
          return failureCount < 2;
        },
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [client] = useState(makeQueryClient);
  return (
    <ThemeContextProvider>
      <QueryClientProvider client={client}>
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          autoHideDuration={5000}
        >
          {children}
        </SnackbarProvider>
      </QueryClientProvider>
    </ThemeContextProvider>
  );
}
